import { bool } from "prop-types";
import { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { useModal } from "Flocknote/stateHooks";

import { useClearActiveAction } from "Dashboard/components/WhatsNext/whatsNextStore";
import { menuTypographyProps } from "NavBar/components/NavLink";
import SettingsOptionWrapper from "NavBar/components/settings/SettingsOptionWrapper";
import SettingsOptionsWrapper from "NavBar/components/settings/SettingsOptionsWrapper";
import { useAddMemberButtonRef } from "NavBar/navBarStore";
import {
  useIsAddMemberButtonFlashing,
  useIsImportMemberButtonFlashing,
} from "NavBar/stateHooks";
import { menuIconProps } from "NavBar/styleConstants";

import FnrShineButton from "UI/FnrButton3/FnrShineButton";
import FnrButtonPopper from "UI/FnrButtonPopper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrTypography from "UI/FnrTypography";
import { marketingColors, navColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import flash from "Utilities/interpolations/flash";

import Button from "./components/Button";
import { hoverTextKeys, useHoverTextKey } from "./toolbarStore";

const AddMember = ({ isNavToolbar = false }) => {
  const [, setHoverTextKey] = useHoverTextKey();
  const [closeTrigger, setCloseTrigger] = useState({});
  const navButtonRef = useAddMemberButtonRef();
  const localRef = useRef();
  const buttonRef = isNavToolbar ? navButtonRef : localRef;
  const isHoverFocused = useIsHoverFocused(buttonRef);
  const [, { openModal }] = useModal();
  const addMemberButtonIsFlashing = useIsAddMemberButtonFlashing();
  const isImportMemberButtonFlashing = useIsImportMemberButtonFlashing();
  const clearActiveAction = useClearActiveAction();

  useEffect(() => {
    if (isHoverFocused) setHoverTextKey(hoverTextKeys.addMember);
    else setHoverTextKey(hoverTextKeys.noHover);
  }, [isHoverFocused, setHoverTextKey]);

  const handleClickEnter = (e) => {
    if (e.type === "click") setCloseTrigger(closeTrigger + 1);
    if (e.key === "Enter") {
      setCloseTrigger({});
      e.currentTarget.querySelector(".add_member_button")?.click();
    }
  };

  return (
    <div>
      <FnrButtonPopper
        ref={buttonRef}
        Button={Button}
        buttonProps={{
          color: navColors.database,
          isFlashing: addMemberButtonIsFlashing || isImportMemberButtonFlashing,
        }}
        closeTrigger={closeTrigger}
        autoFocus
        content={
          <SettingsOptionsWrapper>
            <SFnrShineButton
              $isFlashing={addMemberButtonIsFlashing}
              $flashToColor={addOpacity(marketingColors.lightBlue, 0.2)}
              borderRadius={8}
              onClick={(e) => {
                handleClickEnter(e);
                clearActiveAction();
              }}
              onKeyDown={handleClickEnter}
            >
              <div
                className="add_member_button main_button"
                data-cy="NavBar_AddMemberButton"
                style={{ all: "unset" }} // remove legacy styles
              >
                <SettingsOptionWrapper>
                  <FnrContainedIcon icon="plus fas" {...menuIconProps} />
                  <FnrTypography {...menuTypographyProps}>
                    Add a New Member
                  </FnrTypography>
                </SettingsOptionWrapper>
              </div>
            </SFnrShineButton>

            <SFnrShineButton
              $isFlashing={isImportMemberButtonFlashing}
              $flashToColor={addOpacity(marketingColors.lightBlue, 0.2)}
              borderRadius={8}
              data-cy="PeoplePage_ActionArea_ImportButton"
              onClick={() => {
                openModal("Importing");
                setCloseTrigger({});
                clearActiveAction();
              }}
            >
              <SettingsOptionWrapper>
                <FnrContainedIcon icon="file-import far" {...menuIconProps} />
                <FnrTypography {...menuTypographyProps}>
                  Import Members
                </FnrTypography>
              </SettingsOptionWrapper>
            </SFnrShineButton>
          </SettingsOptionsWrapper>
        }
      >
        <FnrContainedIcon
          color="textLight"
          icon="user-plus fas"
          shadow
          data-cy="NavBar_AddMember"
        />
      </FnrButtonPopper>
    </div>
  );
};

AddMember.propTypes = { isNavToolbar: bool };

const SFnrShineButton = styled(FnrShineButton)`
  ${flash}
`;

export default AddMember;
